import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClientError, type Language } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';
import { RegisterScreenView } from './RegisterScreenView';
import { useOnboardingForm, type FormFieldErrors } from './useOnboardingForm';

/**
 * Container Stage 2 регистрации (`/onboarding/register`, PRD §2.2).
 *
 * State machine (P-021): idle → submitting → success (redirect) | error.
 * `empty` неприменимо к регистрационной форме (нет «список пуст»).
 *
 * Защита маршрута (mount guard):
 *   На mount проверяет `authStore.isOnboarded()`. Если Stage 1 не пройден
 *   (нет lang/city) — `navigate('/onboarding/lang-city')`. Это защищает от
 *   прямого URL-входа на /register без guest-stage. Глобальный auto-cleanup
 *   `if (jwt && !profile) logout()` (ADR-0012 nuance #2) живёт в App.tsx
 *   mount logic — см. Step 5.
 *
 * Submit flow:
 *   1. `useOnboardingForm.validate(lang)` парсит state через
 *      `OnboardingPayloadSchema`. На fail — устанавливает inline errors,
 *      НЕ вызывает API.
 *   2. На success → `apiClient.onboarding.register(payload)`.
 *      На 422 (валидация серверная) — generic form-level error.
 *      На 4xx/5xx ApiClientError — generic form-level error без leak'а
 *      технического сообщения (audit phase B-034 защита).
 *   3. На 2xx → `authStore.setProfile(...)` + `navigate('/')`.
 *
 * P-008 region immutability — клиент НЕ присылает region на сервер
 * (`OnboardingPayloadSchema.strict()` блокирует поле). Region выводится из
 * `payload.city` сервером (server-derived) и возвращается в response.
 *
 * Phase 2 нюанс: `response.region` намеренно не используется Container'ом —
 * клиент полагается на симметричный `CITY_REGION_MAP[city]` (один источник
 * истины и на клиенте, и на MSW server). При расхождении в Phase 9+ (real
 * backend authoritative) Container должен будет читать `response.region`
 * и при mismatch с client-derived — fail safe (logout + alert админу).
 * Зафиксировано как часть будущей миграции на Phase 9 OAuth/OTP backend.
 */
export function RegisterScreen() {
  const navigate = useNavigate();
  const lang = useAuthStore((s) => s.lang);
  const initialCity = useAuthStore((s) => s.city);
  const isOnboarded = useAuthStore((s) => s.isOnboarded);
  const setProfile = useAuthStore((s) => s.setProfile);

  const form = useOnboardingForm({ initialCity });
  const [errors, setErrors] = useState<FormFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Mount guard: без Stage 1 — на Stage 1.
  useEffect(() => {
    if (!isOnboarded()) {
      navigate('/onboarding/lang-city');
    }
  }, [isOnboarded, navigate]);

  // Если Stage 1 не пройден — не рендерим форму (избегаем flash перед redirect).
  // Также narrow'им region: form.region может быть null если city='' (после
  // ручного сброса), но в production flow Stage 1 prefill гарантирует city != ''.
  // Явная проверка здесь — type narrowing вместо type cast.
  if (!isOnboarded() || lang === null || form.region === null) {
    return null;
  }
  const region = form.region;

  const handleSubmit = async () => {
    setErrors({});
    const result = form.validate(lang as Language);
    if (!result.ok || result.payload === undefined) {
      setErrors(result.errors);
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiClient.onboarding.register(result.payload);
      setProfile({
        profile: {
          name: result.payload.name,
          phone: result.payload.phone,
          specialization: result.payload.specialization,
        },
        jwt: response.jwt,
        consents: result.payload.consents,
      });
      navigate('/');
    } catch (err) {
      setSubmitting(false);
      // P-021 error state: generic message, не leak'аем technical detail в UI
      // (см. integration-test.tsx «не leak'ать String(err)» + B-034).
      const message =
        err instanceof ApiClientError
          ? err.code === 'NETWORK_ERROR'
            ? 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.'
            : 'Не удалось завершить регистрацию. Попробуйте ещё раз.'
          : 'Не удалось завершить регистрацию. Попробуйте ещё раз.';
      setErrors({ form: message });
    }
  };

  return (
    <RegisterScreenView
      state={form.state}
      errors={errors}
      region={region}
      submitting={submitting}
      onNameChange={(v) => form.setField('name', v)}
      onCityChange={(v) => form.setField('city', v)}
      onSpecializationChange={(v) => form.setField('specialization', v)}
      onPhoneChange={(v) => form.setField('phone', v)}
      onConsentChange={(key, v) => form.setConsent(key, v)}
      onSubmit={handleSubmit}
    />
  );
}
